/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ActiveReportItem } from '@ecsper/api-client';
import { EcsperApiError } from '@ecsper/api-client';
import { useEcsperAuth } from '@ecsper/auth';
import { useToast } from '@ecsper/ui-primitives';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { AppContext } from '../bootstrap.js';

export type ReportsDashboardContext = Pick<AppContext, 'apiClient'>;

export interface ReportsDashboardProps {
  readonly ctx: ReportsDashboardContext;
}

function formatRelativeTime(iso: string, nowMs: number = Date.now()): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return '';
  }

  const diffSec = Math.round((nowMs - parsed) / 1000);
  if (diffSec < 45) {
    return 'Just now';
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  }

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

function statusBadgeClass(status: string): string {
  const normalised = status.toLowerCase();
  if (normalised === 'active') {
    return 'ecs-badge ecs-badge--active';
  }
  if (normalised === 'skeleton') {
    return 'ecs-badge ecs-badge--skeleton';
  }
  return 'ecs-badge ecs-badge--draft';
}

function initialExpandedState(items: readonly ActiveReportItem[]): Record<string, boolean> {
  const expanded: Record<string, boolean> = {};
  items.forEach((item, index) => {
    expanded[item.item_id] = items.length <= 3 || index === 0;
  });
  return expanded;
}

/** Build a Word ofe launch URL from an API response (full ofe URL or bare HTTPS). */
export function toWordOpenUrl(url: string): string {
  return url.startsWith('ms-word:') ? url : `ms-word:ofe|u|${url}`;
}

function DashboardHeader(props: { readonly onRefresh: () => void }): ReactElement {
  const { onRefresh } = props;
  return (
    <header className="ecs-sidebar__header">
      <p className="ecs-sidebar__title">Your reports</p>
      <button type="button" className="ecs-link" onClick={onRefresh}>
        Refresh
      </button>
    </header>
  );
}

function LoadingSkeleton(): ReactElement {
  return (
    <ul className="ecs-sidebar__list">
      {[0, 1, 2].map((i) => (
        <li key={i} className="ecs-skeleton ecs-skeleton--group" aria-hidden="true" />
      ))}
    </ul>
  );
}

/**
 * Way 2 entry: lists active EW reports grouped by matter when no report context
 * is bound (cold Word open without URL params or custom XML).
 */
export function ReportsDashboard(props: ReportsDashboardProps): ReactElement {
  const { ctx } = props;
  const { state: authState, isAuthenticated } = useEcsperAuth();
  const { toast } = useToast();
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [items, setItems] = useState<readonly ActiveReportItem[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [openingReportId, setOpeningReportId] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  const load = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || authState.contextToken === null || authState.contextToken === '') {
      return;
    }
    setPhase('loading');
    try {
      const response = await ctx.apiClient.getActiveReports();
      const nextItems = response.data.items;
      setItems(nextItems);
      setExpanded(initialExpandedState(nextItems));
      setPhase('ready');
    } catch (e) {
      setPhase('error');
      if (e instanceof EcsperApiError) {
        // eslint-disable-next-line no-console -- dashboard load diagnostics
        console.error('[Ecsper] Failed to load active reports:', e.message);
      } else {
        // eslint-disable-next-line no-console -- dashboard load diagnostics
        console.error('[Ecsper] Failed to load active reports:', e);
      }
    }
  }, [authState.contextToken, ctx.apiClient, isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onFocus = (): void => {
      void load();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const handleOpenReport = useCallback(
    async (reportId: string): Promise<void> => {
      setOpeningReportId(reportId);
      setCardErrors((prev) => {
        const next = { ...prev };
        // reportId is a server-provided UUID used as a map key
        // eslint-disable-next-line security/detect-object-injection -- trusted API identifier
        delete next[reportId];
        return next;
      });
      try {
        const response = await ctx.apiClient.getReportOpenUrl(reportId);
        const launchUrl = toWordOpenUrl(response.data.url);
        // eslint-disable-next-line no-console -- open-in-word diagnostics
        console.info('[Ecsper] Opening report in Word:', launchUrl);
        window.open(launchUrl);
      } catch (error) {
        setCardErrors((prev) => {
          const next = { ...prev };
          // eslint-disable-next-line security/detect-object-injection -- trusted API identifier
          next[reportId] = 'Could not open report. Try again.';
          return next;
        });
        toast('Could not open report. Try again.', 'error');
        // eslint-disable-next-line no-console -- open-in-word diagnostics
        console.error('[Ecsper] Failed to get presigned URL:', error);
      } finally {
        setOpeningReportId(null);
      }
    },
    [ctx.apiClient, toast],
  );

  const toggleGroup = (itemId: string): void => {
    setExpanded((prev) => {
      // eslint-disable-next-line security/detect-object-injection -- trusted API identifier
      const current = prev[itemId] === true;
      return { ...prev, [itemId]: !current };
    });
  };

  const handleRefresh = (): void => {
    void load();
  };

  if (phase === 'loading') {
    return (
      <div className="ecs-sidebar" role="status" aria-busy="true" aria-label="Loading reports">
        <header className="ecs-sidebar__header">
          <p className="ecs-sidebar__title">Your reports</p>
        </header>
        <LoadingSkeleton />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="ecs-sidebar" role="alert">
        <div className="ecs-error">
          <p className="ecs-error__message">Could not load reports.</p>
          <button type="button" className="ecs-btn ecs-btn--secondary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="ecs-sidebar" role="status">
        <DashboardHeader onRefresh={handleRefresh} />
        <p className="ecs-sidebar__empty-text">
          No active reports. Create a report from the Workspace to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="ecs-sidebar">
      <DashboardHeader onRefresh={handleRefresh} />

      <ul role="list" className="ecs-sidebar__list">
        {items.map((item) => {
          const isExpanded = expanded[item.item_id] === true;
          const reportCount = item.reports.length;
          return (
            <li key={item.item_id} role="listitem">
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => {
                  toggleGroup(item.item_id);
                }}
                className="ecs-card ecs-card--interactive"
              >
                <span className="ecs-card__group-main">
                  <span className="ecs-card__title ecs-card__title--truncate">
                    {item.item_title}
                  </span>
                  <span className="ecs-card__ref">{item.item_reference}</span>
                </span>
                <span className="ecs-pill">{reportCount}</span>
              </button>

              {isExpanded ? (
                <ul role="list" className="ecs-sidebar__nested-list">
                  {item.reports.map((report) => {
                    const isOpening = openingReportId === report.id;
                    const cardError = cardErrors[report.id];
                    return (
                      <li key={report.id} role="listitem">
                        <article className="ecs-card ecs-card--report">
                          <p className="ecs-card__title">{report.title}</p>
                          <div className="ecs-card__meta">
                            <span className={statusBadgeClass(report.status)}>{report.status}</span>
                            <span>v{report.version}</span>
                            <span>{formatRelativeTime(report.updated_at)}</span>
                          </div>
                          <div className="ecs-card__actions">
                            <button
                              type="button"
                              className="ecs-btn ecs-btn--primary"
                              disabled={!report.has_docx || isOpening}
                              title={report.has_docx ? undefined : 'No document available'}
                              aria-label={
                                report.has_docx
                                  ? `Open ${report.title}`
                                  : `Open ${report.title} (no document available)`
                              }
                              onClick={() => void handleOpenReport(report.id)}
                            >
                              {isOpening ? 'Opening…' : 'Open'}
                            </button>
                            {cardError !== undefined ? (
                              <p role="alert" className="ecs-card__error">
                                {cardError}
                              </p>
                            ) : null}
                          </div>
                        </article>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
